import { useCallback, useEffect, useState } from 'react';
import { findRoute, groupOf, NavGroupId } from '../../navigation/routes';

export const SIDEBAR_GROUPS_KEY = 'intermal.sidebar.groups';

/* localStorage kann im iframe, im Privatmodus oder in Tests fehlen oder
   werfen -- die Sidebar muss trotzdem funktionieren. */
function readGroups(): Partial<Record<NavGroupId, boolean>> {
  try {
    const raw = window.localStorage.getItem(SIDEBAR_GROUPS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeGroups(groups: Partial<Record<NavGroupId, boolean>>) {
  try {
    window.localStorage.setItem(SIDEBAR_GROUPS_KEY, JSON.stringify(groups));
  } catch {
    /* ignorieren */
  }
}

export interface SidebarState {
  isGroupOpen: (id: NavGroupId) => boolean;
  toggleGroup: (id: NavGroupId) => void;
  mobileOpen: boolean;
  openMobile: () => void;
  closeMobile: () => void;
}

/**
 * Zustand der Seitenleiste: aufgeklappte Gruppen und mobile Schublade.
 * Gruppen sind standardmaessig zu, damit die lange Navigation ueberschaubar
 * bleibt; die Gruppe der aktuellen Seite oeffnet sich bei jedem Seitenwechsel
 * von selbst, alles Weitere merkt sich localStorage. Die Schublade schliesst
 * bei Navigation und Escape.
 */
export function useSidebarState(pathname: string): SidebarState {
  const [groups, setGroups] = useState<Partial<Record<NavGroupId, boolean>>>(readGroups);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isGroupOpen = useCallback((id: NavGroupId) => groups[id] === true, [groups]);

  const toggleGroup = useCallback((id: NavGroupId) => {
    setGroups((prev) => {
      const next = { ...prev, [id]: prev[id] !== true };
      writeGroups(next);
      return next;
    });
  }, []);

  // Die Gruppe der aktuellen Seite ist immer erreichbar: beim Seitenwechsel
  // (auch ueber die Palette) klappt sie auf; zuklappen bleibt danach moeglich.
  useEffect(() => {
    const active = groupOf(findRoute(pathname));
    if (!active) return;
    setGroups((prev) => {
      if (prev[active.id] === true) return prev;
      const next = { ...prev, [active.id]: true };
      writeGroups(next);
      return next;
    });
  }, [pathname]);

  const openMobile = useCallback(() => setMobileOpen(true), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  // Navigation schliesst die Schublade -- sonst verdeckt sie die neue Seite.
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('app-drawer-open');
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('app-drawer-open');
    };
  }, [mobileOpen]);

  return { isGroupOpen, toggleGroup, mobileOpen, openMobile, closeMobile };
}
